import React, { useState, useEffect, useRef } from 'react';
import { Menu, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Section {
  id: string;
  title: string;
}

const sections: Section[] = [
  { id: 'overview', title: 'Overview' },
  { id: 'problem', title: 'Problem & Insight' },
  { id: 'solution', title: 'Solution & Process' },
  { id: 'results', title: 'Results & Opportunity' },
  { id: 'reflection', title: 'Reflection' }
];

function VerifyKicks() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-100px 0px -60% 0px',
        threshold: [0, 0.3, 0.5, 0.7, 1]
      }
    );

    sections.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="w-full h-[280px] md:h-[350px] lg:h-[420px] overflow-hidden">
        <img
          src="/header.png"
          alt="Verify Kicks"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="flex relative">
        {isSidebarOpen && (
          <aside className="sticky top-0 h-screen w-80 bg-zinc-950 border-r border-white/10 overflow-y-auto">
            <div className="p-8">
              <div className="space-y-6">
                <div className="flex items-center gap-2">
                  <Link
                    to="/"
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-zinc-800 text-white/80 hover:bg-zinc-700 hover:text-white transition-all"
                  >
                    <Home size={18} />
                  </Link>
                  <button
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-zinc-800 text-white/80 hover:bg-zinc-700 hover:text-white transition-all"
                  >
                    <Menu size={18} />
                  </button>
                </div>
                <div className="text-white text-sm">Verify Kicks</div>
                <nav className="space-y-1">
                  {sections.map(({ id, title }) => (
                    <button
                      key={id}
                      onClick={() => scrollToSection(id)}
                      className={`w-full text-left px-3 py-2 text-sm transition-all ${
                        activeSection === id
                          ? 'text-blue-500 border-l-2 border-blue-500'
                          : 'text-white/60 hover:text-white border-l-2 border-transparent'
                      }`}
                    >
                      {title}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </aside>
        )}

        {!isSidebarOpen && (
          <div className="sticky top-0 h-fit pt-6 pl-6">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-zinc-900/90 backdrop-blur-xl text-white/80 hover:bg-zinc-800 hover:text-white transition-all border border-white/10"
            >
              <Menu size={18} />
            </button>
          </div>
        )}

        <main className={`flex-1 max-w-6xl p-4 py-20 transition-all duration-300 ${isSidebarOpen ? 'ml-auto mr-32' : 'mx-auto'}`}>
        <div className="space-y-20">

          <section id="overview" className="space-y-6 scroll-mt-20">
            <h2 className="text-3xl md:text-4xl text-white font-serif">Overview</h2>
            <div className="space-y-4 text-white/70 leading-relaxed">
              <p>
                Verify Kicks is an AI-powered sneaker authentication app that helps buyers instantly confirm whether
                their sneakers are real or fake.
              </p>
              <p>
                I led the product vision, strategy, and user validation process - working with AI engineers who built and
                trained the computer-vision model.
              </p>
              <p>
                Our goal was simple: make sneaker authentication fast, trustworthy, and accessible for everyday buyers
                who shop through online resellers or marketplaces.
              </p>
            </div>
          </section>

          <section id="problem" className="space-y-6 scroll-mt-20">
            <h2 className="text-3xl md:text-4xl text-white font-serif">Problem & Insight</h2>
            <div className="space-y-4 text-white/70 leading-relaxed">
              <p>
                Counterfeit sneakers are everywhere. Sneaker fans often lose money and confidence when they
                unknowingly buy fake pairs online. Manual authentication services exist, but they're slow, expensive, and
                not scalable.
              </p>
              <p>During user research, I found three consistent pain points:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Trust issues – buyers couldn't be sure their sneakers were real.</li>
                <li>Manual verification – experts took days to respond.</li>
                <li>Financial risk – people lost hundreds on fakes.</li>
              </ul>
              <p>
                I validated these insights through 15 interviews with sneaker enthusiasts and resellers.
                80% of respondents said they'd use an app for quick authentication, and two sneaker influencers
                offered to promote it.
              </p>
            </div>
          </section>

          <div className="w-full">
            <img
              src="/painpoints.png"
              alt="Pain Points Visualization"
              className="w-full max-w-2xl mx-auto rounded-2xl"
            />
          </div>

          <section id="solution" className="scroll-mt-20">
            <h2 className="text-3xl md:text-4xl text-white font-serif mb-6">Solution & Process</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>
                  We designed a simple three-step experience:
                  <br />
                  Scan → Verify → Trust.
                </p>
                <p>
                  Users upload multi-angle photos of their sneakers, and within minutes, they receive a
                  result showing whether the pair is authentic — along with a confidence score and heat-map
                  visualization.
                </p>
                <p>
                  I defined the product flow, prioritized features, and collaborated with engineers to translate
                  sneaker-specific details into technical requirements. Our AI team used YOLOv12 for
                  image detection, built the FastAPI backend, and deployed the system on AWS S3 and MongoDB.
                </p>
                <p className="pt-4">From a product standpoint, my focus was on:</p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>Creating a frictionless upload-and-check experience.</li>
                  <li>Building user trust through transparent results.</li>
                  <li>Introducing tiered pricing based on urgency - Saver (24 hrs), Flex (12 hrs), and Anytime (instant).</li>
                </ul>
                <p>
                  This balance of simplicity, transparency, and speed made Verify Kicks feel both credible and useful.
                </p>
              </div>
              <div className="flex items-center justify-center">
                <iframe
                  src="https://drive.google.com/file/d/1hjCV63s095aLb_tYyXjufDNaOtPsXNPl/preview"
                  width="400"
                  height="300"
                  allow="autoplay"
                  className="rounded-2xl shadow-2xl"
                ></iframe>
              </div>
            </div>
          </section>

          <section id="results" className="space-y-6 scroll-mt-20">
            <h2 className="text-3xl md:text-4xl text-white font-serif">Results & Opportunity</h2>
            <div className="space-y-4 text-white/70 leading-relaxed">
              <p>Our validation results were strong:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>80% willingness to use the app</li>
                <li>70 NPS score, showing high satisfaction</li>
                <li>2 influencers ready to promote the product</li>
              </ul>
              <p>
                We estimated a $9B global market for sneaker authentication, with a realistic $15M obtainable segment in the first few years.
              </p>
              <p>
                The pricing model ranged from $2.99 to $19.99 per check, offering both affordability and scalability.
              </p>
              <p>
                Looking ahead, there's clear potential to expand into B2B API integrations for resale platforms, enabling
                automated verification before listings go live.
              </p>
            </div>
          </section>

          <div className="w-full">
            <img
              src="/market.png"
              alt="Market Opportunity Visualization"
              className="w-full max-w-2xl mx-auto rounded-2xl"
            />
          </div>

          <section id="reflection" className="space-y-6 scroll-mt-20 pb-20">
            <h2 className="text-3xl md:text-4xl text-white font-serif">Reflection</h2>
            <div className="space-y-4 text-white/70 leading-relaxed">
              <p>
                Verify Kicks taught me how to bridge AI technology and real-world trust.
                As a product manager, I learned to translate complex ML concepts into clear user value — focusing on
                transparency, usability, and emotional reassurance.
              </p>
              <p>
                This project reminded me that technology alone isn't enough; people need to <em>believe</em> the result.
                That's where product design, communication, and trust intersect.
              </p>
            </div>
          </section>
        </div>
        </main>
      </div>
    </div>
  );
}

export default VerifyKicks;
